import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MagnifyingGlassIcon, StarIcon, TrashIcon, ViewColumnsIcon, ArrowsRightLeftIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { BurstViewer } from './BurstViewer';
import StorageTierBadge from './StorageTierBadge';
import MigrationDialog from './MigrationDialog';
import { FlashMessage } from './FlashMessage';
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts';

interface Photo {
  photo_id: string;
  filename: string;
  thumbnail_url?: string;
  capture_date: string;
  camera?: string;
  lens?: string;
  status: string;
  storage_tier?: 'INSTANT' | 'STANDARD' | 'ARCHIVE';
  burst_id?: string;
  burst_group?: {
    burst_id: string;
    photo_count: number;
  };
  paths?: {
    thumbnail_small?: string;
    thumbnail_medium?: string;
    thumbnail_large?: string;
  };
  exif?: {
    camera_model?: string;
    lens_model?: string;
    focal_length?: number;
    aperture?: number;
    exposure_time?: string;
    iso?: number;
  };
}

interface PhotoGridProps {
  photos: Photo[];
  onPhotoClick: (photo: Photo) => void;
  onPhotoFlag: (photoId: string) => void;
  onPhotoCull: (photoId: string) => void;
  onLoadMore?: () => void;
}

export const PhotoGrid: React.FC<PhotoGridProps> = ({
  photos,
  onPhotoClick,
  onPhotoFlag,
  onPhotoCull,
  onLoadMore,
}) => {
  const [selectedPhotos, setSelectedPhotos] = useState<Set<string>>(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState<string | null>(null);
  const [filterStorageTier, setFilterStorageTier] = useState<string | null>(null);
  const [selectedBurstId, setSelectedBurstId] = useState<string | null>(null);
  const [showMigrationDialog, setShowMigrationDialog] = useState(false);
  const [targetTier, setTargetTier] = useState<'INSTANT' | 'STANDARD' | 'ARCHIVE'>('STANDARD');
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);

  // Filter photos based on search, status, and storage tier
  const filteredPhotos = photos.filter(photo => {
    const camera = photo.camera || photo.exif?.camera_model || '';
    const lens = photo.lens || photo.exif?.lens_model || '';

    const matchesSearch = searchQuery === '' ||
      photo.filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
      camera.toLowerCase().includes(searchQuery.toLowerCase()) ||
      lens.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter = filterStatus === null || photo.status === filterStatus;
    const matchesStorageTier = filterStorageTier === null || photo.storage_tier === filterStorageTier;

    return matchesSearch && matchesFilter && matchesStorageTier;
  });

  // Keyboard shortcuts integration
  const { flashMessage } = useKeyboardShortcuts({
    photos: filteredPhotos,
    selectedPhotos,
    currentPhotoIndex,
    onNavigate: (direction) => {
      setCurrentPhotoIndex(prev => {
        if (direction === 'prev') return Math.max(0, prev - 1);
        return Math.min(filteredPhotos.length - 1, prev + 1);
      });
    },
    onFlag: onPhotoFlag,
    onCull: onPhotoCull,
    onToggleSelection: (photoId) => {
      const newSelected = new Set(selectedPhotos);
      if (newSelected.has(photoId)) {
        newSelected.delete(photoId);
      } else {
        newSelected.add(photoId);
      }
      setSelectedPhotos(newSelected);
    },
    onSelectAll: () => {
      setSelectedPhotos(new Set(filteredPhotos.map(p => p.photo_id)));
    },
    onBurstToggle: (burstId) => {
      setSelectedBurstId(burstId);
    },
    enabled: !selectedBurstId, // Disable when burst viewer is open
  });

  const handlePhotoSelect = (photoId: string, event: React.MouseEvent) => {
    if (event.shiftKey && selectedPhotos.size > 0) {
      // Shift+click: select range
      const photoIds = filteredPhotos.map(p => p.photo_id);
      const lastSelected = Array.from(selectedPhotos).pop();
      const start = photoIds.indexOf(lastSelected!);
      const end = photoIds.indexOf(photoId);
      const range = photoIds.slice(Math.min(start, end), Math.max(start, end) + 1);
      setSelectedPhotos(new Set([...Array.from(selectedPhotos), ...range]));
    } else if (event.metaKey || event.ctrlKey) {
      // Cmd/Ctrl+click: toggle selection
      const newSelected = new Set(selectedPhotos);
      if (newSelected.has(photoId)) {
        newSelected.delete(photoId);
      } else {
        newSelected.add(photoId);
      }
      setSelectedPhotos(newSelected);
    } else {
      // Regular click: open photo
      onPhotoClick(filteredPhotos.find(p => p.photo_id === photoId)!);
    }
  };

  const handleMigratePhotos = async () => {
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'https://api.photosynthesize.app';
      const response = await fetch(`${API_URL}/storage/migrate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({
          photo_ids: Array.from(selectedPhotos),
          target_tier: targetTier,
        }),
      });

      if (!response.ok) {
        throw new Error('Migration failed');
      }

      // Success - close dialog and clear selection
      setShowMigrationDialog(false);
      setSelectedPhotos(new Set());

      // Refresh photos to show updated tiers
      window.location.reload(); // Simple refresh for now
    } catch (error) {
      console.error('Migration error:', error);
      alert('Failed to migrate photos. Please try again.');
    }
  };

  return (
    <div className="h-full flex flex-col bg-neutral-50 dark:bg-neutral-900">
      {/* Flash Message for Keyboard Shortcuts */}
      {flashMessage && (
        <FlashMessage
          message={flashMessage.message}
          type={flashMessage.type}
        />
      )}

      {/* Header with Search and Filters */}
      <div className="sticky top-0 z-10 bg-white dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700 shadow-sm">
        <div className="px-6 py-4">
          <div className="flex items-center gap-4">
            {/* Search */}
            <div className="flex-1 relative">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-neutral-400" />
              <input
                type="text"
                placeholder="Search by filename, camera, or lens..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-900 dark:text-neutral-100 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-shadow"
              />
            </div>

            {/* Storage Tier Filter */}
            <select
              value={filterStorageTier || ''}
              onChange={(e) => setFilterStorageTier(e.target.value || null)}
              className="px-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-600 transition-colors font-medium"
            >
              <option value="">All Storage Tiers</option>
              <option value="INSTANT">⚡ Instant</option>
              <option value="STANDARD">📦 Standard</option>
              <option value="ARCHIVE">🗄️ Archive</option>
            </select>

            {/* Status Filter */}
            <select
              value={filterStatus || ''}
              onChange={(e) => setFilterStatus(e.target.value || null)}
              className="px-4 py-2 rounded-lg border border-neutral-300 dark:border-neutral-600 bg-white dark:bg-neutral-700 text-neutral-700 dark:text-neutral-200 hover:bg-neutral-50 dark:hover:bg-neutral-600 transition-colors font-medium"
            >
              <option value="">All Status</option>
              <option value="uploaded">Uploaded</option>
              <option value="flagged_for_edit">Flagged for Edit</option>
              <option value="culled">Culled</option>
              <option value="edited">Edited</option>
              <option value="published">Published</option>
            </select>

            {/* Results Count */}
            <div className="text-sm text-neutral-600 dark:text-neutral-400">
              {filteredPhotos.length} {filteredPhotos.length === 1 ? 'photo' : 'photos'}
            </div>

            {/* Keyboard Shortcuts Hint */}
            <div className="text-xs text-neutral-500 dark:text-neutral-500 font-mono border-l border-neutral-300 dark:border-neutral-600 pl-4">
              <span className="hidden lg:inline">
                ← → Navigate • <kbd className="px-1.5 py-0.5 bg-neutral-200 dark:bg-neutral-700 rounded">K</kbd> Keep • <kbd className="px-1.5 py-0.5 bg-neutral-200 dark:bg-neutral-700 rounded">D</kbd> Cull • <kbd className="px-1.5 py-0.5 bg-neutral-200 dark:bg-neutral-700 rounded">X</kbd> Select • <kbd className="px-1.5 py-0.5 bg-neutral-200 dark:bg-neutral-700 rounded">Space</kbd> Burst
              </span>
            </div>
          </div>

          {/* Bulk Actions (shown when photos selected) */}
          <AnimatePresence>
            {selectedPhotos.size > 0 && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-4 flex items-center gap-3 px-4 py-3 bg-primary-50 dark:bg-primary-900/20 rounded-lg border border-primary-200 dark:border-primary-800"
              >
                <span className="text-sm font-medium text-primary-900 dark:text-primary-100">
                  {selectedPhotos.size} selected
                </span>
                <div className="flex gap-2 ml-auto">
                  <select
                    value={targetTier}
                    onChange={(e) => setTargetTier(e.target.value as 'INSTANT' | 'STANDARD' | 'ARCHIVE')}
                    className="px-3 py-1.5 rounded-md border border-primary-300 dark:border-primary-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-200 text-sm font-medium"
                  >
                    <option value="INSTANT">Move to ⚡ Instant</option>
                    <option value="STANDARD">Move to 📦 Standard</option>
                    <option value="ARCHIVE">Move to 🗄️ Archive</option>
                  </select>
                  <button
                    onClick={() => setShowMigrationDialog(true)}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    <ArrowsRightLeftIcon className="w-4 h-4" />
                    Migrate
                  </button>
                  <button
                    onClick={() => {
                      selectedPhotos.forEach(id => onPhotoFlag(id));
                      setSelectedPhotos(new Set());
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-primary-400 text-white hover:bg-primary-500 transition-colors text-sm font-medium"
                  >
                    <StarIcon className="w-4 h-4" />
                    Flag for Edit
                  </button>
                  <button
                    onClick={() => {
                      selectedPhotos.forEach(id => onPhotoCull(id));
                      setSelectedPhotos(new Set());
                    }}
                    className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-neutral-600 text-white hover:bg-neutral-700 transition-colors text-sm font-medium"
                  >
                    <TrashIcon className="w-4 h-4" />
                    Cull
                  </button>
                  <button
                    onClick={() => setSelectedPhotos(new Set())}
                    className="px-3 py-1.5 rounded-md text-neutral-700 dark:text-neutral-300 hover:bg-neutral-200 dark:hover:bg-neutral-700 transition-colors text-sm font-medium"
                  >
                    Cancel
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      {/* Photo Grid */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4">
          <AnimatePresence>
            {filteredPhotos.map((photo, index) => (
              <PhotoCard
                key={photo.photo_id}
                photo={photo}
                index={index}
                isSelected={selectedPhotos.has(photo.photo_id)}
                isCurrent={index === currentPhotoIndex}
                onClick={(e) => {
                  handlePhotoSelect(photo.photo_id, e);
                  setCurrentPhotoIndex(index);
                }}
                onFlag={() => onPhotoFlag(photo.photo_id)}
                onCull={() => onPhotoCull(photo.photo_id)}
                onBurstClick={(burstId) => setSelectedBurstId(burstId)}
              />
            ))}
          </AnimatePresence>
        </div>

        {/* Empty State */}
        {filteredPhotos.length === 0 && (
          <div className="flex flex-col items-center justify-center h-96 text-center">
            <div className="w-24 h-24 rounded-full bg-neutral-100 dark:bg-neutral-800 flex items-center justify-center mb-4">
              <MagnifyingGlassIcon className="w-12 h-12 text-neutral-400" />
            </div>
            <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
              No photos found
            </h3>
            <p className="text-neutral-600 dark:text-neutral-400 max-w-md">
              {searchQuery ? `No photos match "${searchQuery}"` : 'Upload your first photos to get started'}
            </p>
          </div>
        )}
      </div>

      {/* BurstViewer Modal */}
      <AnimatePresence>
        {selectedBurstId && (
          <BurstViewer
            burstId={selectedBurstId}
            onClose={() => setSelectedBurstId(null)}
          />
        )}
      </AnimatePresence>

      {/* Migration Dialog */}
      <MigrationDialog
        isOpen={showMigrationDialog}
        onClose={() => setShowMigrationDialog(false)}
        onConfirm={handleMigratePhotos}
        photoIds={Array.from(selectedPhotos)}
        targetTier={targetTier}
      />
    </div>
  );
};

interface PhotoCardProps {
  photo: Photo;
  index: number;
  isSelected: boolean;
  isCurrent?: boolean;
  onClick: (e: React.MouseEvent) => void;
  onFlag: () => void;
  onCull: () => void;
  onBurstClick: (burstId: string) => void;
}

const PhotoCard: React.FC<PhotoCardProps> = ({
  photo,
  index,
  isSelected,
  isCurrent = false,
  onClick,
  onFlag,
  onCull,
  onBurstClick,
}) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);

  const hasBurst = !!(photo.burst_id || photo.burst_group?.burst_id);
  const burstCount = photo.burst_group?.photo_count;

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.2, delay: index * 0.02 }}
      className="group relative"
    >
      <div
        className={`
          relative aspect-square rounded-lg overflow-hidden cursor-pointer
          transition-all duration-200
          ${isSelected ? 'ring-4 ring-primary-400 scale-95' : isCurrent ? 'ring-2 ring-blue-400' : 'hover:scale-105 hover:shadow-lg'}
          ${!imageLoaded ? 'bg-neutral-200 dark:bg-neutral-700 animate-pulse' : ''}
        `}
        onClick={onClick}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Photo Image */}
        <img
          src={
            photo.thumbnail_url ||
            (photo as any).thumbnail_urls?.thumbnail_medium ||
            (photo as any).thumbnail_urls?.thumbnail_small ||
            photo.paths?.thumbnail_medium ||
            photo.paths?.thumbnail_small ||
            ''
          }
          alt={photo.filename}
          loading="lazy"
          onLoad={() => setImageLoaded(true)}
          onError={(e) => {
            console.error('Failed to load image for photo:', photo.photo_id, photo);
            setImageLoaded(true); // Show placeholder even on error
          }}
          className={`
            w-full h-full object-cover
            transition-opacity duration-300
            ${imageLoaded ? 'opacity-100' : 'opacity-0'}
          `}
        />

        {/* Status Badge */}
        {photo.status === 'FLAGGED_FOR_EDIT' && (
          <div className="absolute top-2 right-2">
            <div className="flex items-center gap-1 px-2 py-1 rounded-md bg-primary-400 text-white text-xs font-medium shadow-lg">
              <StarSolidIcon className="w-3 h-3" />
              Flagged
            </div>
          </div>
        )}

        {photo.status === 'CULLED' && (
          <div className="absolute top-2 right-2">
            <div className="px-2 py-1 rounded-md bg-neutral-800 text-white text-xs font-medium shadow-lg">
              Culled
            </div>
          </div>
        )}

        {/* Burst Badge */}
        {hasBurst && (
          <div className="absolute top-2 left-2">
            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={(e) => {
                e.stopPropagation();
                const burstId = photo.burst_id || photo.burst_group?.burst_id;
                if (burstId) onBurstClick(burstId);
              }}
              className="flex items-center gap-1 px-2 py-1 rounded-md bg-blue-500 text-white text-xs font-medium shadow-lg hover:bg-blue-600 transition-colors"
              title="View burst timeline"
            >
              <ViewColumnsIcon className="w-3 h-3" />
              {burstCount ? `${burstCount}` : 'Burst'}
            </motion.button>
          </div>
        )}

        {/* Storage Tier Badge */}
        {photo.storage_tier && (
          <div className={`absolute left-2 ${hasBurst ? 'top-12' : 'top-2'}`}>
            <StorageTierBadge tier={photo.storage_tier} />
          </div>
        )}

        {/* Selection Checkbox - Always visible on hover */}
        {(isSelected || isHovered) && (
          <div
            className="absolute bottom-2 right-2 z-20"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              // Create a synthetic event with metaKey to trigger toggle
              const syntheticEvent = { ...e, metaKey: true } as React.MouseEvent;
              onClick(syntheticEvent);
            }}
          >
            <div className={`
              w-7 h-7 rounded-md flex items-center justify-center cursor-pointer
              ${isSelected ? 'bg-primary-500' : 'bg-white/90 backdrop-blur-sm'}
              border-2 ${isSelected ? 'border-primary-500' : 'border-neutral-300'}
              transition-all hover:scale-110 shadow-lg
            `}>
              {isSelected && (
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              )}
            </div>
          </div>
        )}

        {/* Hover Overlay */}
        <AnimatePresence>
          {isHovered && !isSelected && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"
            >
              {/* Photo Info */}
              <div className="absolute bottom-0 left-0 right-0 p-3">
                <p className="text-white font-medium text-sm truncate mb-1">
                  {photo.filename}
                </p>
                <p className="text-white/80 text-xs font-mono mb-2">
                  {photo.camera || photo.exif?.camera_model || 'Unknown'} · {photo.lens || photo.exif?.lens_model || 'Unknown'}
                </p>
                <p className="text-white/70 text-xs font-mono">
                  {photo.exif?.exposure_time || 'N/A'} · f/{photo.exif?.aperture || 'N/A'} · ISO {photo.exif?.iso || 'N/A'} · {photo.exif?.focal_length || 'N/A'}mm
                </p>
              </div>

              {/* Quick Actions */}
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex gap-2">
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onFlag();
                  }}
                  className="p-2 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors shadow-lg"
                  title="Flag for editing"
                >
                  {photo.status === 'FLAGGED_FOR_EDIT' ? (
                    <StarSolidIcon className="w-5 h-5 text-primary-400" />
                  ) : (
                    <StarIcon className="w-5 h-5 text-neutral-700" />
                  )}
                </motion.button>
                <motion.button
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    onCull();
                  }}
                  className="p-2 rounded-full bg-white/90 backdrop-blur-sm hover:bg-white transition-colors shadow-lg"
                  title="Cull photo"
                >
                  <TrashIcon className="w-5 h-5 text-neutral-700" />
                </motion.button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.div>
  );
};
